import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  healthAttentionFgClass,
  healthAttentionIconTileClass,
} from '@up2stay/up2promo-ui';
import { AlertCircle } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import { resolveLinkErrorView } from '@/routes/link-error-copy';

const VALID_REASONS = [
  'missingGooglePlace',
  'missingInstagram',
  'missingFacebook',
  'missingTiktok',
  'missingMenu',
  'missingProfile',
  'locationNotFound',
] as const;

type LinkErrorReason = (typeof VALID_REASONS)[number];

const TITLE_CLASS = 'text-[15.5px] font-bold leading-tight text-foreground';
const BODY_CLASS = 'text-[13.5px] leading-relaxed text-muted-foreground';

function parseReason(raw: string | null): LinkErrorReason | null {
  if (!raw) return null;
  return VALID_REASONS.includes(raw as LinkErrorReason)
    ? (raw as LinkErrorReason)
    : null;
}

export function LinkRedirectErrorPage() {
  const dict = useDictionary();
  const linkErrorDict = (dict as { linkError?: Record<string, string> })
    .linkError;
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const reason = parseReason(searchParams.get('reason'));
  const hash = searchParams.get('hash')?.trim() ?? '';

  const isSignedIn = useAuthStore((state) =>
    Boolean(state.tokens?.accessToken),
  );
  const view = resolveLinkErrorView({
    reasonMessage: (reason && linkErrorDict?.[reason]) || undefined,
    description: linkErrorDict?.description ?? 'This link is not ready yet.',
    isSignedIn,
  });

  const isProfileCardIssue = reason === 'missingProfile';
  const linkCardHref = hash
    ? `${shellPaths.profilesLinkCard}?hash=${encodeURIComponent(hash)}`
    : shellPaths.profilesLinkCard;
  const productsHref = hash
    ? `${shellPaths.moreProducts}?hash=${encodeURIComponent(hash)}`
    : shellPaths.moreProducts;
  const secondaryHref = isProfileCardIssue ? linkCardHref : productsHref;
  const secondaryLabel = isProfileCardIssue
    ? (linkErrorDict?.linkMyCard ?? 'Link my card')
    : (linkErrorDict?.viewProducts ?? 'Open products');

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md overflow-hidden">
        <CardHeader className="gap-3 p-5 text-center sm:p-6">
          <div
            className={cn(
              'mx-auto flex h-12 w-12 items-center justify-center rounded-2xl',
              healthAttentionIconTileClass,
            )}
          >
            <AlertCircle className="h-6 w-6" aria-hidden />
          </div>
          <CardTitle className={TITLE_CLASS}>
            {linkErrorDict?.title ?? "This link isn't ready yet"}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 px-5 pb-5 text-center sm:px-6 sm:pb-6">
          <p className={BODY_CLASS}>{view.primary}</p>
          {view.secondary ? (
            <p className={BODY_CLASS}>{view.secondary}</p>
          ) : null}
          <p
            className={cn(
              'text-[11px] font-semibold uppercase tracking-[0.6px]',
              healthAttentionFgClass,
            )}
          >
            {isProfileCardIssue
              ? view.showSignIn
                ? linkErrorDict?.memberHint
                : linkErrorDict?.memberHintSignedIn
              : view.showSignIn
                ? linkErrorDict?.managerHint
                : linkErrorDict?.managerHintSignedIn}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            {view.showSignIn ? (
              <Button asChild variant="primary">
                <Link to="/auth">{linkErrorDict?.signIn ?? 'Sign in'}</Link>
              </Button>
            ) : null}
            <Button asChild variant={view.showSignIn ? 'outline' : 'primary'}>
              <Link to={secondaryHref}>{secondaryLabel}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
